import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgCheckbox,
  IgPaper,
  IgTextField,
} from "@intelligems/ig-theme";
import { Grid } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { styled } from "@mui/material/styles";

import { useSwitchWidgetOrganization } from "../../api/organizations/use-switch-widget-organization";
import { useLogin } from "../../api/user-auths/login";
import {
  clearRememberedEmail,
  rememberEmail,
  selectedIsShopLogin,
  selectedRememberEmail,
  setEmailLogin,
  setShopLogin,
} from "../../store/slices/appSettings";
import { IUser } from "../../types/user";

const PREFIX = "UserPasswordLogin";

const classes = {
  form: `${PREFIX}-form`,
  container: `${PREFIX}-container`,
  paper: `${PREFIX}-paper`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.form}`]: {
    width: "100%", // Fix IE 11 issue.
  },

  [`&.${classes.container}`]: {
    // marginTop: theme.spacing(1),
  },

  [`& .${classes.paper}`]: {
    minHeight: "100px",
    padding: "10px 10px 10px 10px",
  },
}));

const initialUser: IUser = {
  email: "",
  password: "",
};

export const UserPasswordLogin = () => {
  const isShopLogin = useSelector(selectedIsShopLogin);
  const login = useLogin();
  const dispatch = useDispatch();
  const urlParams = new URLSearchParams(window.location.search);
  const widgetAuth = urlParams.get("widgetAuth");
  const switchOrganization = useSwitchWidgetOrganization();

  const rememberedEmail = useSelector(selectedRememberEmail);

  const [isRememberEmail, setIsRememberEmail] =
    useImmer<boolean>(!!rememberedEmail);

  const [user, setUser] = useState<IUser>({
    ...initialUser,
    email: rememberedEmail || "",
  });

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (isRememberEmail) {
      dispatch(rememberEmail(user.email));
    } else {
      dispatch(clearRememberedEmail());
    }
    if (user.email && user.password) {
      login.mutate(
        {
          email: user.email.trim(),
          password: user.password,
          ...(widgetAuth ? { orgId: widgetAuth, token: "" } : {}),
        },
        {
          onSuccess: (data) => {
            if (data.type === "login" && widgetAuth) {
              void switchOrganization.mutateAsync({
                token: data.token,
                orgId: widgetAuth,
              });
            }
          },
        }
      );
    }
  };

  return (
    <StyledGrid
      className={classes.container}
      container
      direction={"column"}
      rowSpacing={2}
    >
      <Grid item xs>
        <IgPaper className={classes.paper}>
          <form className={classes.form} onSubmit={handleSubmit}>
            <IgTextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              value={user.email}
              onChange={(e) =>
                setUser({ ...user, email: e.currentTarget.value })
              }
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
            />
            <IgTextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              value={user.password}
              onChange={(e) =>
                setUser({ ...user, password: e.currentTarget.value })
              }
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
            />
            <FormControlLabel
              control={
                <IgCheckbox
                  value="remember"
                  color="primary"
                  checked={isRememberEmail}
                  onChange={(e) => setIsRememberEmail(e.target.checked)}
                />
              }
              label="Remember me"
            />
            <IgButton
              loading={login.isPending}
              variant={"contained"}
              fullWidth
              type="submit"
              size={"medium"}
            >
              Sign In
            </IgButton>
          </form>
        </IgPaper>
      </Grid>
      <Grid item xs style={{ width: "100%" }}>
        <IgButton
          fullWidth
          size={"medium"}
          variant={"text"}
          onClick={() =>
            dispatch(isShopLogin ? setEmailLogin() : setShopLogin())
          }
        >
          Log in with Shopify
        </IgButton>
      </Grid>
    </StyledGrid>
  );
};
